import React, { useRef, useEffect, useState, useCallback } from 'react';
import Map, { Source, Layer, Marker, Popup, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import finishIcon from '../../components/base/finish.svg';
import startIcon from '../../components/base/start.svg';
import { t } from 'i18next';
import StringUtils from '../../utils/StringUtils';
import MapLibreUtils from '../../utils/MaplibreUtils';
import haversine from 'haversine-distance';
import PropTypes from 'prop-types';
import { buildToleranceCircleGeoJson, getToleranceMeters } from '../../utils/GeoMapUtils';

const calculateRouteBounds = (route) => {
    const coordinates = route.flatMap((r) => r.coordinates);
    if (!coordinates.length) {
        return null;
    }
    const longitudes = coordinates.map((c) => c[0]);
    const latitudes = coordinates.map((c) => c[1]);
    const minLon = Math.min(...longitudes);
    const maxLon = Math.max(...longitudes);
    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const latSpan = maxLat - minLat || 0.001;
    const lonSpan = maxLon - minLon || 0.001;
    const padLat = Math.max(latSpan * 0.2, 0.001);
    const padLon = Math.max(lonSpan * 0.2, 0.001);
    return [[minLon - padLon, minLat - padLat], [maxLon + padLon, maxLat + padLat]];
};

const fitMapToRouteBounds = (mapRef, route) => {
    const routeBounds = calculateRouteBounds(route);
    if (!routeBounds || !mapRef?.current) {
        return;
    }
    const map = mapRef.current.getMap?.() ?? mapRef.current;
    if (!map?.fitBounds) {
        return;
    }
    map.resize?.();
    map.fitBounds(routeBounds, { padding: 48, maxZoom: 17, duration: 0 });
};

const MapWithRouteV2 = React.memo(({ route, distance, averageSpeed, companyLocations = [] }) => {
    const [popupInfo, setPopupInfo] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(10);
    const [geoJsonData, setGeoJsonData] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef();
    
    const meterForSegment = (zoom = zoomLevel) => {
        if(zoom >= 12 && zoom < 13) return 10;
        else if(zoom >= 11 && zoom < 12) return 20;
        else if(zoom >= 10 && zoom < 11) return 35;
        else if(zoom >= 9 && zoom < 10) return 50;
        else return 10;
    }
    
   const createGeoJson = useCallback((zoom = zoomLevel) => {
        let jsonData = {} 
        if(zoom <= 9.1) jsonData = getForSmallZoom();
        else jsonData = getForBigZoom(meterForSegment(zoom));;
        setGeoJsonData(jsonData);
    }, [route, zoomLevel]); 


    useEffect(() => {
        if(StringUtils.isBlank(geoJsonData) ){
            createGeoJson();
        }
    }, [route, zoomLevel, geoJsonData, createGeoJson]);

    useEffect(() => {
        if (mapLoaded && route.length > 0) {
            fitMapToRouteBounds(mapRef, route);
        }
    }, [mapLoaded, route]);

    const flagMarkerIconStart = {
        url: startIcon,
        width: 32,
        height: 32
    };

    const flagMarkerIconFinish = {
        url: finishIcon,
        width: 32,
        height: 32
    };

    const getForSmallZoom = () => {
        const coordinates = coordinatesToOneSegment();
        return {
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    geometry: {
                      type: 'LineString',
                      coordinates: coordinates,
                    },
                    properties: {
                      description: `<div>
                                      <div>${t('global.avarageSpeed')}: <b>${averageSpeed?.toFixed(2)} km/h</b></div>
                                      <div>${t('global.distance')}: <b>${distance?.toFixed(2)} km</b></div>
                                    </div>`,
                      color: MapLibreUtils.getColorBySpeed(averageSpeed?.toFixed(2)) ,
                    },
                }
            ]
        };
    }
    
    const coordinatesToOneSegment = ()=>{
        const result = route.flatMap(singleRoute => 
            singleRoute.coordinates.flatMap(coordinate => [
              coordinate
            ])
          );
        const uniqueCoordinates = new Set(result.map(JSON.stringify));  
        const uniqueCoordinatesArray = Array.from(uniqueCoordinates).map(JSON.parse);
        return uniqueCoordinatesArray;
    }

    const getForBigZoom = (meterIndicator = 10) =>{
        const optimizedSegments = coordinatesToOptimize(route, meterIndicator);
        return {
            type: 'FeatureCollection',
            features: optimizedSegments.map((segment) => {
                return ({
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: segment.coordinates,
                },
                properties: {
                description: `<div>
                    <div>${t('global.avarageSpeed')}: <b>${segment.averageSpeed?.toFixed(2)} km/h</b></div>
                        <div>${t('global.distance')}: <b>${segment.distance?.toFixed(2)} km</b></div>
                    </div>`,
                    color: segment.color || '#0000FF',
                },
            })}),
        };
    }

    const coordinatesToOptimize = (routes, maxDistance = 10) => {
        const mergedRoutes = []; 
        let currentRoute = { ...routes[0] }; 
        for (let i = 1; i < routes.length; i++) {
            const nextRoute = routes[i];
            const startPointCurrent = currentRoute.coordinates[0];
            const startPointNext = nextRoute.coordinates[0];
            const distanceBetweenStartPoints = haversine(
                { lat: startPointCurrent[1], lon: startPointCurrent[0] },
                { lat: startPointNext[1], lon: startPointNext[0] }
            ); 
            if (distanceBetweenStartPoints <= maxDistance) {
                currentRoute.coordinates = [
                    currentRoute.coordinates[0], 
                    nextRoute.coordinates[nextRoute.coordinates.length - 1], 
                ];
                currentRoute.distance += nextRoute.distance;
                currentRoute.durationInMinutes += nextRoute.durationInMinutes;
                const avarageSpeed =   (currentRoute.averageSpeed + nextRoute.averageSpeed) / 2
                currentRoute.averageSpeed = avarageSpeed;
                currentRoute.color = MapLibreUtils.getColorBySpeed(avarageSpeed);
            } else {
                mergedRoutes.push(currentRoute);
                currentRoute = { ...nextRoute };
            }
        }
        mergedRoutes.push(currentRoute);
        return mergedRoutes;
    }

    if (route.length === 0) {
        return null;
    }
    const startPoint = route[0].coordinates[0];
    const endPoint = route[route.length - 1].coordinates[route[route.length - 1].coordinates.length - 1];
    
    const syncZoomFromMap = (zoom) => {
        if (zoom == null || Number.isNaN(zoom)) {
            return;
        }
        setZoomLevel((prev) => (Math.abs(prev - zoom) < 0.01 ? prev : zoom));
        createGeoJson(zoom);
    };

    const handleZoomChange = (event) => {
        syncZoomFromMap(event.viewState.zoom);
    };

    const handleMouseMove = (event) => {
        const map = mapRef.current.getMap();
        const features = map.queryRenderedFeatures(event.point, {
            layers: route.map((_) => `route-layer`)
        });
        if (features.length > 0) {
            const hoveredFeature = features[0];
            setPopupInfo({
                coordinates:{
                    lng:event.lngLat.lng,
                    lat:event.lngLat.lat
                },
                description:hoveredFeature.properties.description
                })
            return;
        }
        if(!StringUtils.isBlank(popupInfo))
            setPopupInfo(null)
    };
    const routeBounds = calculateRouteBounds(route);
    const routeCenter = routeBounds
        ? [
            (routeBounds[0][0] + routeBounds[1][0]) / 2,
            (routeBounds[0][1] + routeBounds[1][1]) / 2,
        ]
        : [startPoint[0], startPoint[1]];
    const validCompanyLocations = (companyLocations || []).filter(
        (l) =>
            l != null &&
            l.latitude != null &&
            l.longitude != null &&
            !Number.isNaN(Number(l.latitude)) &&
            !Number.isNaN(Number(l.longitude))
    );
    return (
        <Map
            ref={mapRef}
            initialViewState={{
                longitude: routeCenter[0],
                latitude: routeCenter[1],
                zoom: 14,
            }}
            onLoad={() => {
                setMapLoaded(true);
                const map = mapRef.current?.getMap?.() ?? mapRef.current;
                requestAnimationFrame(() => fitMapToRouteBounds(mapRef, route));
                const applyZoom = () => syncZoomFromMap(map.getZoom());
                map.once('idle', applyZoom);
            }}
            onZoom={handleZoomChange}
            onMoveEnd={handleZoomChange}
            style={{ height: '600px', zIndex: '100' }}
            mapStyle={process.env.REACT_APP_MAP_STYLE}
            onMouseMove={handleMouseMove}
        >
            <NavigationControl position="top-right" />
            <Source id="route-source" type="geojson" data={geoJsonData}>
                <Layer
                    id="route-layer"
                    type="line"
                    paint={{
                        'line-color': ['get', 'color'],
                        'line-width': 4,
                    }}
                />
            </Source>
            {validCompanyLocations.map((loc, index) => {
                const lat = Number(loc.latitude);
                const lon = Number(loc.longitude);
                const meters = getToleranceMeters(loc.localizationTolerance);
                return (
                    <Source
                        key={`company-tolerance-${loc.id ?? index}`}
                        id={`company-tolerance-${loc.id ?? index}`}
                        type="geojson"
                        data={buildToleranceCircleGeoJson(lat, lon, meters)}
                    >
                        <Layer
                            id={`company-tolerance-fill-${loc.id ?? index}`}
                            type="fill"
                            paint={{
                                'fill-color': 'rgba(66, 135, 245, 0.28)',
                            }}
                        />
                        <Layer
                            id={`company-tolerance-outline-${loc.id ?? index}`}
                            type="line"
                            paint={{
                                'line-color': '#4287f5',
                                'line-width': 2,
                            }}
                        />
                    </Source>
                );
            })}
            <Marker longitude={startPoint[0]} latitude={startPoint[1]} anchor="bottom">
                <img src={flagMarkerIconStart.url} alt="Start Icon" width={32} height={32} />
            </Marker>
            <Marker longitude={endPoint[0]} latitude={endPoint[1]} anchor="bottom">
                <img src={flagMarkerIconFinish.url} alt="Finish Icon" width={32} height={32} />
            </Marker>
            {popupInfo && (
                <Popup
                    longitude={popupInfo.coordinates.lng}
                    latitude={popupInfo.coordinates.lat}
                    closeButton={false}
                    closeOnClick={false}
                    anchor="top"
                >
                <div dangerouslySetInnerHTML={{ __html: popupInfo.description }} />
                </Popup>
            )}
        </Map>
    );
});

MapWithRouteV2.propTypes = {
    route: PropTypes.array.isRequired,
    distance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    averageSpeed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    companyLocations: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
            localizationTolerance: PropTypes.number,
            displayName: PropTypes.string,
        })
    ),
};

MapWithRouteV2.defaultProps = {
    companyLocations: [],
};

export default MapWithRouteV2;